import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getBillingMetrics } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  ArrowClockwise,
  Bank,
  ChartBar,
  CurrencyDollar,
  DownloadSimple,
  Receipt,
  TrendDown,
  TrendUp,
  Truck,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { exportBillingMetricsToPDF } from '../utils/exportUtils';
import { MetricCard, StatsGrid } from '../components/app';

const money = (value) => `$${Number(value || 0).toLocaleString()}`;

function BreakdownTable({ title, icon: Icon, columns, rows, emptyLabel }) {
  return (
    <div className="valhalla-card p-4 sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-[#E2B05E]" />
        <h2 className="font-semibold text-[#F3F4F6]">{title}</h2>
      </div>

      {rows?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[420px]">
            <thead>
              <tr className="border-b border-[#242E42]">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`py-2 text-sm font-medium text-[#9CA3AF] ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="border-b border-[#242E42]/50">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`py-3 text-sm ${column.align === 'right' ? 'text-right' : 'text-left'} ${column.className || 'text-[#F3F4F6]'}`}
                    >
                      {column.render ? column.render(row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="py-6 text-center text-sm text-[#9CA3AF]">{emptyLabel}</p>
      )}
    </div>
  );
}

export default function BillingDashboardPage() {
  const { isSpanish } = useLanguage();
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getBillingMetrics({ period });
      setMetrics(data);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar overview' : 'Error loading overview');
    } finally {
      setLoading(false);
    }
  }, [isSpanish, period]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const periodLabels = {
    week: isSpanish ? 'Esta semana' : 'This week',
    month: isSpanish ? 'Este mes' : 'This month',
    quarter: isSpanish ? 'Este trimestre' : 'This quarter',
    year: isSpanish ? 'Este año' : 'This year',
  };

  if (loading && !metrics) {
    return (
      <Layout title="Overview">
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#E2B05E]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Overview">
      <div className="space-y-6" data-testid="billing-overview-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6]">
              Overview
            </h1>
            <p className="mt-1 text-[#9CA3AF]">
              {isSpanish
                ? 'Entradas y salidas principales de facturación.'
                : 'Core billing money in and money out.'}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-full valhalla-input sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#242E42] bg-[#151B26]">
                <SelectItem value="week">{isSpanish ? 'Esta semana' : 'This week'}</SelectItem>
                <SelectItem value="month">{isSpanish ? 'Este mes' : 'This month'}</SelectItem>
                <SelectItem value="quarter">{isSpanish ? 'Este trimestre' : 'This quarter'}</SelectItem>
                <SelectItem value="year">{isSpanish ? 'Este año' : 'This year'}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadMetrics} className="border-[#242E42] text-[#9CA3AF]">
                <ArrowClockwise className="h-4 w-4" />
              </Button>
              {metrics && (
                <Button variant="outline" onClick={() => exportBillingMetricsToPDF(metrics, periodLabels[period])} className="border-[#242E42] text-[#9CA3AF]">
                  <DownloadSimple className="mr-2 h-4 w-4" />
                  PDF
                </Button>
              )}
            </div>
          </div>
        </div>

        <StatsGrid className="md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label={isSpanish ? 'Dinero entrante' : 'Money in'}
            value={money(metrics?.money_in)}
            icon={TrendUp}
            tone="success"
          />
          <MetricCard
            label={isSpanish ? 'Dinero saliente' : 'Money out'}
            value={money(metrics?.money_out)}
            icon={TrendDown}
            tone="danger"
          />
          <MetricCard
            label={isSpanish ? 'Flujo neto' : 'Net flow'}
            value={money(metrics?.net_cash_flow)}
            icon={Bank}
            tone={(metrics?.net_cash_flow || 0) >= 0 ? 'accent' : 'danger'}
          />
          <MetricCard
            label={isSpanish ? 'Cuentas por cobrar' : 'Accounts receivable'}
            value={money(metrics?.accounts_receivable)}
            icon={Receipt}
            tone="info"
          />
        </StatsGrid>

        <StatsGrid className="md:grid-cols-3 xl:grid-cols-3">
          <MetricCard
            label={isSpanish ? 'Facturado' : 'Invoiced'}
            value={money(metrics?.total_invoiced)}
            icon={ChartBar}
            tone="info"
          />
          <MetricCard
            label={isSpanish ? 'Gastos contables' : 'Accounting expenses'}
            value={money(metrics?.accounting_expenses)}
            icon={CurrencyDollar}
            tone="danger"
          />
          <MetricCard
            label={isSpanish ? 'Pagos a proveedores' : 'Supplier payments'}
            value={money(metrics?.supplier_payments)}
            icon={Truck}
            tone="warning"
          />
        </StatsGrid>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <BreakdownTable
            title={isSpanish ? 'Ingresos cobrados por cliente' : 'Collected revenue by client'}
            icon={Receipt}
            emptyLabel={isSpanish ? 'Sin facturas pagadas en este periodo' : 'No paid invoices in this period'}
            columns={[
              { key: 'client_name', label: isSpanish ? 'Cliente' : 'Client' },
              { key: 'count', label: isSpanish ? 'Facturas' : 'Invoices', align: 'right', className: 'text-[#9CA3AF]' },
              { key: 'paid', label: isSpanish ? 'Cobrado' : 'Collected', align: 'right', className: 'font-semibold text-emerald-400', render: (row) => money(row.paid) },
            ]}
            rows={metrics?.by_client}
          />

          <BreakdownTable
            title={isSpanish ? 'Pagos a proveedores' : 'Supplier payments'}
            icon={Truck}
            emptyLabel={isSpanish ? 'Sin pagos a proveedores en este periodo' : 'No supplier payments in this period'}
            columns={[
              { key: 'supplier_name', label: isSpanish ? 'Proveedor' : 'Supplier' },
              { key: 'count', label: isSpanish ? 'Pagos' : 'Payments', align: 'right', className: 'text-[#9CA3AF]' },
              { key: 'paid', label: isSpanish ? 'Pagado' : 'Paid', align: 'right', className: 'font-semibold text-red-400', render: (row) => money(row.paid) },
            ]}
            rows={metrics?.by_supplier}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <BreakdownTable
            title={isSpanish ? 'Gastos por categoría' : 'Expenses by category'}
            icon={TrendDown}
            emptyLabel={isSpanish ? 'Sin gastos contables en este periodo' : 'No accounting expenses in this period'}
            columns={[
              { key: 'category', label: isSpanish ? 'Categoría' : 'Category' },
              { key: 'amount', label: isSpanish ? 'Monto' : 'Amount', align: 'right', className: 'font-semibold text-red-400', render: (row) => money(row.amount) },
            ]}
            rows={metrics?.by_expense_category}
          />

          <BreakdownTable
            title={isSpanish ? 'Facturas pagadas recientes' : 'Recent paid invoices'}
            icon={CurrencyDollar}
            emptyLabel={isSpanish ? 'Sin facturas pagadas recientes' : 'No recent paid invoices'}
            columns={[
              { key: 'invoice_number', label: isSpanish ? 'Factura' : 'Invoice', className: 'font-mono text-[#E2B05E]' },
              { key: 'client_name', label: isSpanish ? 'Cliente' : 'Client', className: 'text-[#F3F4F6]' },
              { key: 'total', label: isSpanish ? 'Total' : 'Total', align: 'right', className: 'font-semibold text-emerald-400', render: (row) => money(row.total) },
            ]}
            rows={metrics?.recent_paid_invoices}
          />
        </div>
      </div>
    </Layout>
  );
}
